import { API } from '@xbto/api-client';
import { MarketsDataAggregatorType } from '../../utils';

export const getSimplifiedListAssetCodes = (
  listResult: API.SimplifiedListAsset[] | null
) => {
  const result: string[] = [];
  if (!listResult) {
    return result;
  }
  for (const item of listResult) {
    if (!item.code) {
      continue;
    }
    result.push(item.code);
  }
  return result;
};

export const processMarketsData = async (
  marketListResult: API.SimplifiedListAsset[] | null,
  marketsDataAggregator: MarketsDataAggregatorType,
  apiClient: API.StablehouseClient
): Promise<{
  result: API.ListAssetDetailsResponse[];
  errorMessage: string | null;
}> => {
  const _assetCodes = getSimplifiedListAssetCodes(marketListResult);

  if (!_assetCodes.length) {
    return {
      result: [],
      errorMessage: null,
    };
  }

  /**
   * Note:
   * When a user switches a tab,
   * we want to fetch the details on first pass immediately for FMP reasons
   * This happens below:
   * 1. if it is in the aggregator we get it form there
   * 2. for the rest we make an xhr call
   *
   * Over the lifetime of the app the aggregator will have all the data via `ws` updating & fetch/ rest api will become rare
   */
  const { hasAllData, assetCodesWithoutData, aggregatedData } =
    marketsDataAggregator.get(_assetCodes);
  const result: API.ListAssetDetailsResponse[] = [];

  if (!hasAllData) {
    const detailsResponse = await apiClient.listAssetDetails({
      assets: assetCodesWithoutData,
      /**
       * Note:
       * if we pass the list responses page options here, we will get empty array on the details,
       * as the details is simply an endpoint to get optionally more info and is not married to paging
       */
      page: 1,
      pageSize: assetCodesWithoutData.length,
    });

    if (
      detailsResponse.errorCode ||
      !detailsResponse.isSuccessful ||
      !detailsResponse.result
    ) {
      return {
        result: [],
        errorMessage: detailsResponse.errorMessage,
      };
    }

    const combined = [...aggregatedData, ...detailsResponse.result];
    for (const _code of _assetCodes) {
      const _item = combined.find(c => c.code === _code);
      if (!_item) {
        continue;
      }
      result.push(_item);
    }
  } else {
    result.push(...aggregatedData);
  }

  marketsDataAggregator.receive(assetCodesWithoutData);

  return {
    result,
    errorMessage: null,
  };
};

export const trackMarketsData = (
  marketsDataAggregator: MarketsDataAggregatorType,
  shouldSkip: () => boolean,
  getMarketData: () => API.ListAssetDetailsResponse[] | null | undefined,
  onData: (data: API.ListAssetDetailsResponse[]) => void
) => {
  marketsDataAggregator.feed.subscribe(value => {
    if (shouldSkip()) {
      // console.log(`skip market data update`);
      return;
    }
    if (!value || !value.code) {
      return;
    }

    const result = getMarketData();
    const _dataHasValue = result?.find(d => d.code === value.code);
    if (!_dataHasValue) {
      return;
    }

    console.debug(
      `Updating for ${value.code} ${value.data?.cryptoMarketDetail?.price}`
    );
    const newResult = result?.map(r => {
      if (r.code === value.code) {
        return value;
      }
      return r;
    });
    onData(newResult || []);
  });
};
